/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { Mdx, Image, Link, ScrollTracking } from '@ta-interaktiv/contentbricks'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import Methodik from 'components/methodik'

// TODO Set the correct article ID here
const ARTICLE_ID = '147144460867'
const isMobile = window.innerWidth < 600

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <ScrollTracking />
      <header className='ui vertical very fitted segment'>
        <div className='ui container'>
          <h1 className='ui image header title'>
            <div className='content title'>
              Kommt jetzt die nächste Welle? Was die Lockerung für Auswirkungen
              haben könnte
              <div className='sub header lead'>
                In der Schweiz hat sich die Ausbreitung des Coronavirus stark
                verlangsamt. Welchen Einfluss die Massnahmen des Bundesrats
                hatten und was nun mit der Lockerung auf uns zukommen könnte.
              </div>
            </div>
          </h1>
        </div>
      </header>

      <div className='content'>
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='metainfo'>
              <div className='author'>
                <nobr>Titus Plattner</nobr>, <nobr>Mathias Lutz</nobr>,{' '}
                <nobr>Marc Brupbacher</nobr>
              </div>
              <div className='updated'>Aktualisiert am 13. Mai</div>
            </div>
            <p>
              Die Coronavirus-Ansteckungszahlen in der Schweiz konnten stark
              gebremst werden. Die Massnahmen zur Eindämmung der
              Covid-19-Pandemie greifen. Dies zeigen verschiedene Modelle,
              welche die sogenannte Reproduktionszahl berechnen.
            </p>
            <p className='box-beige'>
              <h4>
                Reproduktionszahl <nobr>(R-Wert)</nobr>
              </h4>
              Reproduktionszahl nennen Fachleute die Anzahl Menschen, die eine
              infizierte Person insgesamt ansteckt. Ist der Wert grösser als 1,
              breitet sich ein Erreger exponentiell aus. Darunter sinkt die Zahl
              der Infizierten, und es kann gelingen, eine Epidemiewelle zu
              stoppen.
            </p>
            <p>
              <a href='https://ispmbern.github.io/covid-19/swiss-epidemic-model/'>
                Epidemienforscher Christian Althaus
              </a>{' '}
              gibt den R-Wert zurzeit für die Schweiz mit 0,3 an. Das ist so
              tief, wie es damals in Wuhan nach dem kompletten Shutdown gemessen
              wurde. Zum Vergleich: Vor dem 17. März berechnete er diesen Wert
              für die Schweiz mit 2,8 – im Schnitt steckte also eine Person
              damals fast drei andere mit Sars-Cov-2 an.
            </p>
            <p>
              Auch die ETH Zürich berechnet die Reproduktionszahl. «Momentan
              liegt der Wert gemäss unseren Berechnungen bei rund 0,7», sagt
              Tanja Stadler. Die ETH-Professorin am Departement für Biosysteme
              in Basel{' '}
              <a href='https://bsse.ethz.ch/cevo/research/sars-cov-2/real-time-monitoring-in-switzerland.html'>
                analysiert
              </a>{' '}
              mit ihrem Team fast in Echtzeit die Reproduktionszahlen in der
              Schweiz. Ein Infizierter steckt also momentan weniger als eine
              Person an. Das muss so bleiben, sonst steigen die Fallzahlen
              wieder schneller. Gemäss Stadler besteht also ein Spielraum bis
              maximal 40 Prozent für eine Lockerung der Massnahmen.
            </p>
          </div>
          <div className='ui text container chart'>
            <span className='current'>Stand: 13. Mai 2020</span>
            <h3>Entwicklung R-Wert in der Schweiz, ETH-Modell</h3>
            <div className='subtitle'>
              Anzahl Personen, die ein Infizierter durchschnittlich ansteckt.
              Aufgrund der Verzögerungen zwischen Infektion und Beobachtung
              zeigt die Grafik eine Schätzung mit einer Verzögerung von 10
              Tagen.
            </div>
            <div className='legend'>
              <img src={require('./images/grossveranstaltungen.svg')} />
              <strong>28. Februar: </strong> Verbot Veranstaltungen >1000
              Personen
            </div>
            <div className='legend'>
              <img src={require('./images/hygiene.svg')} />
              <strong>2. März: </strong> Hygiene- und Verhaltensregeln
            </div>
            <div className='legend'>
              <img src={require('./images/schulen.svg')} />
              <strong>13. März: </strong> Schulen werden geschlossen, Verbot
              Versammlungen <nobr>>100 Personen</nobr>
            </div>
            <div className='legend'>
              <img src={require('./images/lockdown.svg')} />
              <strong>16. März: </strong> Notstand für die ganze Schweiz,
              Lockdown
            </div>
            <Image file={require('./images/schweiz-eth.svg')} />
            <div className='source'>
              Quelle:{' '}
              <a href='https://bsse.ethz.ch/cevo/research/sars-cov-2/real-time-monitoring-in-switzerland.html'>
                cEvo, ETH Zürich
              </a>
            </div>

            <Methodik title='Methodik'>
              Die ETH-Forscher um Tanja Stadler berechneten die
              Reproduktionszahl aufgrund der Anzahl positiver Sars-CoV-2-Tests,
              welche die Kantone täglich bekannt geben. Die zeitliche
              Verzögerung zwischen einer Infektion und einem positiven
              Testergebnis wird mit 8 bis 12 Tagen angenommen. Ein Problem bei
              der Analyse der Neuansteckungen ist, dass diese fehlerhaft sein
              können. So könnten Kapazitätsprobleme und auch das Verhalten von
              Infizierten und Ärzten die Zahl der Tests und damit auch der
              positiven Messungen beeinflussen. Für ihre Untersuchung haben die
              ETH-Forscher deshalb vergleichbare Berechnungen mit der Anzahl
              Hospitalisationen und den Covid-19-Todesfällen durchgeführt.
              «Beides sind zuverlässige Kenngrössen, die weniger stark vom
              Problem einer Dunkelziffer betroffen sind», so Stadler.
            </Methodik>
          </div>

          <div className='ui text container'>
            <p>
              Was für einige Kontroversen sorgte, war die Tatsache, dass die
              Reproduktionszahl beim ETH-Modell schon vor dem Lockdown am 16.
              März deutlich kleiner wurde. Der St. Galler Chefarzt Pietro
              Vernazza schreibt in einem{' '}
              <a href='https://infekt.ch/2020/04/sind-wir-tatsaechlich-im-blindflug/'>
                Diskussionsbeitrag
              </a>
              : «Hygiene ist alles, der Rest nur Zugabe.» War damit der harte
              Einschnitt mit Schliessungen von Schulen, Restaurants und Läden
              überhaupt nötig? Stadler sagt dazu: «Der R-Wert konnte tatsächlich
              schon vorher deutlich gedrückt werden. Es ist aber leichter, von
              ganz oben nach unten zukommen, als das letzte Quäntchen zu
              schaffen. Social Distancing, Hygienemassnahmen und
              Selbstquarantäne hatten einen sehr positiven Effekt. Deutlich und
              nachhaltig unter den Wert 1 fiel die Reproduktionszahl aber erst
              nach dem Lockdown und nach dem wenige Tage später erlassenen
              Versammlungsverbot für mehr als fünf Personen.» Stadlers Resultate
              zeigen, dass die R-Zahl vor dem Lockdown über 1 war, was ohne
              weitere Massnahmen oder gravierende Verhaltensänderungen weiterhin
              ein exponentielles Wachstum bedeutet hätte und damit auch eine
              Überlastung des Gesundheitssystems.
            </p>

            <p>
              Zu diesem Schluss kommt auch{' '}
              <a href='https://mrc-ide.github.io/covid19estimates/#/details/Switzerland'>
                das Modell des Imperial College London
              </a>
              , das für 14 Länder – unter anderem die Schweiz – den R-Wert
              laufend berechnet. Erst der Lockdown brachte den Durchbruch beim
              Abflachen der Kurve der Neuinfektionen. In Schweden ist zu sehen,
              dass die Reproduktionszahl zwar auch unter 1 rutschte, aber später
              und nicht so deutlich wie in der Schweiz. Schweden verfolgt ein
              sehr moderates Massnahmenkonzept bei der Bekämpfung der Epidemie.
            </p>
          </div>
          <div className='ui text container chart'>
            <span className='current'>Stand: 13. Mai 2020</span>
            <h3>Entwicklung R-Wert in der Schweiz, Modell Imperial College</h3>
            <div className='subtitle'>
              Anzahl Personen, die ein Infizierter durchschnittlich ansteckt.
            </div>
            <div className='legend'>
              <img src={require('./images/grossveranstaltungen.svg')} />
              <strong>28. Februar: </strong> Verbot Veranstaltungen >1000
              Personen
            </div>
            <div className='legend'>
              <img src={require('./images/hygiene.svg')} />
              <strong>2. März: </strong> Hygiene- und Verhaltensregeln
            </div>
            <div className='legend'>
              <img src={require('./images/schulen.svg')} />
              <strong>13. März: </strong> Schulen werden geschlossen, Verbot
              Versammlungen <nobr>>100 Personen</nobr>
            </div>
            <div className='legend'>
              <img src={require('./images/lockdown.svg')} />
              <strong>16. März: </strong> Notstand für die ganze Schweiz,
              Lockdown
            </div>
            <Image file={require('./images/schweiz.svg')} />
            <div className='source'>
              Quelle:{' '}
              <a href='https://mrc-ide.github.io/covid19estimates/#/details/Switzerland'>
                Covid-19 model, Imperial College London
              </a>
            </div>
          </div>

          <div className='ui text container chart'>
            <span className='current'>Stand: 13. Mai 2020</span>
            <h3>Entwicklung R-Wert in Schweden, Modell Imperial College</h3>
            <div className='subtitle'>
              Anzahl Personen, die ein Infizierter durchschnittlich ansteckt.
            </div>
            <div className='legend'>
              <img src={require('./images/hygiene.svg')} />
              <strong>10. März: </strong> Verhaltensregeln bei
              Krankheitssymptomen
            </div>
            <div className='legend'>
              <img src={require('./images/hygiene.svg')} />
              <strong>16. März: </strong> Homeoffice-Empfehlung Region Stockholm
            </div>
            <div className='legend'>
              <img src={require('./images/grossveranstaltungen.svg')} />
              <strong>29. März: </strong> Verbot Versammlungen >50 Personen
            </div>
            <Image file={require('./images/schweden.svg')} />
            <div className='source'>
              Quelle:{' '}
              <a href='https://mrc-ide.github.io/covid19estimates/#/details/Sweden'>
                Covid-19 model, Imperial College London
              </a>
            </div>
          </div>

          <div className='ui text container'>
            <p>
              «Das Imperial College verwendet für die Berechnung ein komplett
              anderes Modell als wir und berücksichtigt nur die Todesfälle, wir
              schauen auch die Zahl der Neuinfektionen und Hospitalisierten an.
              Und trotzdem kommen beide zum gleichen Schluss: Die
              Reproduktionszahl war vor dem Lockdown deutlich über dem Wert von
              1, er kam erst danach unter diese wichtige Schwelle», sagt
              Stadler.
            </p>
            <p>
              ETH-Forscher haben auch die Lockdown-Massnahmen in 20 Ländern
              verglichen und{' '}
              <a href='https://www.medrxiv.org/content/10.1101/2020.04.16.20062141v1.full.pdf'>
                analysiert
              </a>
              , was die Ansteckungen am effizientesten absinken lässt. Am
              meisten bringt demnach die Schliessung von Geschäften,
              Restaurants, Kinos und anderen Kultureinrichtungen. Diese könnte
              gemäss den Schätzungen 30 Prozent der Ansteckungen im Laufe einer
              Woche verhindern. Einen ähnlich hohen Nutzen errechneten die
              Wissenschaftler für Arbeitseinschränkungen, Grenzschliessungen
              sowie Versammlungs- und Veranstaltungsverbote.{' '}
              <a href='https://www.tagesanzeiger.ch/schulschliessungen-bringen-weniger-als-ein-restaurant-lockdown-179607399773'>
                Weniger deutlich war der Effekt für Schulschliessungen
              </a>{' '}
              und komplette Ausgehverbote, wie sie zum Beispiel in Italien
              verordnet wurden. Bei diesen Massnahmen betrug die
              Ansteckungsreduktion im Mittel zwischen 5 und 11 Prozent.
            </p>
          </div>

          <div className='ui text container chart'>
            <AutofitIFrame
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/2UwWY'
            />
          </div>

          <div className='ui text container'>
            <p>
              Nun wurden die Massnahmen in der Schweiz etwas gelockert,
              Baumärkte und Coiffeursalons sind wieder offen. Um wie viel wird
              der R-Wert nun wieder nach oben gehen? Ist bald wieder mit einem
              exponentiellen Wachstum zu rechnen? Genaue Aussagen seien hier
              schwierig zu machen, sagt Stadler. «Wichtig ist, dass man
              schrittweise lockert, danach findet man heraus, welche Massnahmen
              etwas bringen. Ansonsten kommt es wieder zum Lockdown.»
            </p>
            <p>
              Wie sich die Lockerungen auf die Entwicklung der Epidemie und vor
              allem die Belastung in den Spitälern auswirken wird, hat ein Team
              aus Forschern der ETH Lausanne und der Johns Hopkins University in
              Baltimore <a href='https://jcblemai.github.io/'>modelliert</a>.
            </p>
            <p>
              Für die Wissenschaftler ist klar: Den R-Wert unter 1 zu halten,
              wird extrem schwierig werden. Und wenn das nicht gelingt, wird es
              eine zweite Welle geben, die länger und möglicherweise höher
              ausfallen wird als jene, welche wir nun mit drastischen
              Eindämmungsmassnahmen künstlich zum Abflachen gebracht haben.
            </p>
            <p>
              Je nach gewählten Parametern könnte die zweite Kurve im Juli oder
              August ihren Höhepunkt erreichen und das Schweizer Spitalsystem
              sehr nahe an seine Kapazitätsgrenzen bringen. In den letzten
              Monaten wurde die Anzahl Betten auf Intensivstationen von
              ursprünglich 981 auf 2000 aufgestockt. Am Ende dieser Welle würden
              in der Schweiz 5 bis 6 Millionen Menschen an Covid-19 erkranken,
              es gäbe 15'000 bis 21'000 Todesfälle.
            </p>
            <p>
              Und dieses Szenario ist noch nicht das schlimmste. Es handelt sich
              um ein Modell, das auf einer Reproduktionsrate der Krankheit von
              1,5 basiert. Das bedeutet, dass jeder Patient im Durchschnitt 1
              bis 2 Personen infizieren würde.
            </p>
            <p>
              Ein optimistischeres Szenario geht von einem R-Wert von 1,2 aus.
              Dies würde in einem sehr langen, flachen Höhepunkt resultieren:
              800 Intensivbetten wären ständig zwischen Juli und September
              besetzt. Insgesamt würden 5000 bis 15’000 Menschen in der Schweiz
              sterben, und 2 bis 5 Millionen wären am Ende infiziert. «Leider
              ist dies ein sehr realistisches Szenario», sagt Professor Jacques
              Fellay von der EPFL, der die Studie betreut hat. Der Arzt und
              Genetikforscher ist Mitglied der vom Bundesrat eingesetzten
              wissenschaftlichen Taskforce. «Einige Festivals im Sommer zu
              verbieten wird den R0-Wert nie unter 1,2 bringen.»
            </p>
          </div>

          <div className='ui text container chart'>
            <span className='current'>Stand: 27. April 2020</span>
            <h3>
              Reichen die Kapazitäten auf den Schweizer Intensivstationen?
            </h3>
            <div className='subtitle'>
              Anzahl belegter Betten auf Intensivstationen für 3 Szenarien
            </div>
            <Image file={require('./images/icu.svg')} />
            <div className='source'>
              Quelle:{' '}
              <a href='https://jcblemai.github.io'>
                Switzerland COVID-19 Scenario Report
              </a>
              , EPFL Joseph Lemaitre et al. und Johns Hopkins University
            </div>
          </div>

          <div className='ui text container'>
            <p>
              Das Modell, das an der ETH Lausanne mitentwickelt wurde, wird von
              vielen Behörden im Ausland als Referenzmodell benutzt. Joseph
              Lemaitre, der die Berechnungen durchführt, macht jede Nacht
              Hochrechnungen, die den amerikanischen Entscheidungsträgern
              vorbehalten sind. Der Gouverneur von Kalifornien zeigte einige
              Auszüge davon auf Pressekonferenzen. «In der Schweiz stehen die
              Menschen den Berechnungen der Epidemiologen viel skeptischer
              gegenüber», so Lemaitre. «Modelle sind keine Kristallkugeln, die
              uns die Zukunft vorhersagen, sondern Instrumente, die es uns
              ermöglichen, besser vorauszusehen. Diese Modelle zu ignorieren,
              wäre ein gravierender Fehler.»
            </p>
            <p>
              «Ich gehe davon aus, dass es in der Schweiz immer wieder neue
              Ausbrüche geben wird, die möglicherweise sogar zu lokalen
              Lockdowns führen könnten», so Lemaitre. Die regionalen
              Differenzen, die wir zurzeit in der Schweiz noch sehen, würden
              verschwinden. Die Zahl der künftigen Fälle werde im Lande ziemlich
              ausgeglichen sein. «Das Virus ist jetzt flächendeckend in allen
              Kantonen verteilt.» Und weiter: «Unsere Ergebnisse sind
              bemerkenswert konsistent. Alle Gruppen, die die Epidemie in der
              Schweiz modellieren, kommen zu sehr ähnlichen Schlussfolgerungen,
              dass die Epidemie im Sommer wieder erstarken wird.»
            </p>
            <p>
              Ab dieser Woche begeben wir uns in der Schweiz ins Ungewisse. Die
              Erfahrung wird zeigen, ob wir zu viel und zu schnell öffnen oder
              ob wir es uns leisten können, die Massnahmen Stück für Stück
              weiter zu lockern.
            </p>
          </div>
          <div className='ui text container chart'>
            <span className='current'>Stand: 27. April 2020</span>
            <AutofitIFrame
              aria-label='Interactive line chart'
              src='//datawrapper.dwcdn.net/CVNgw'
            />
          </div>
        </div>
      </div>

      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie verständlich waren die Grafiken?'
        ]}
        credits={[
          ['Text, Umsetzung', 'Marc Brupbacher, Titus Plattner, Mathias Lutz']
        ]}
      >
        <div
          className='ui container'
          style={{ margin: isMobile ? '1.2em 0 3em' : '0 0 3em' }}
        >
          <h4 className='teaser'>Weitere Beiträge zum Thema</h4>
          <ArticleTeasers
            numberOfCards='two'
            articleIds={['18727947', '533675517866']}
          />
        </div>
      </Footer>
    </div>
  )
}
